package at.theatervogelweide.admin.web.screens.acts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import at.theatervogelweide.admin.contract.ImageCategory
import at.theatervogelweide.admin.contract.acts.ActDraft
import at.theatervogelweide.admin.contract.acts.CastPerson
import at.theatervogelweide.admin.web.AdminApi
import at.theatervogelweide.admin.web.components.AdminBadge
import at.theatervogelweide.admin.web.components.AdminButton
import at.theatervogelweide.admin.web.components.AdminCallout
import at.theatervogelweide.admin.web.components.AdminCard
import at.theatervogelweide.admin.web.components.AdminDateField
import at.theatervogelweide.admin.web.components.AdminImageUploader
import at.theatervogelweide.admin.web.components.AdminMarkdownField
import at.theatervogelweide.admin.web.components.AdminSingleImageField
import at.theatervogelweide.admin.web.components.AdminTextAreaField
import at.theatervogelweide.admin.web.components.AdminTextField
import at.theatervogelweide.admin.web.components.AdminTimeField
import at.theatervogelweide.admin.web.components.AdminTokenInput
import at.theatervogelweide.admin.web.components.BadgeVariant
import at.theatervogelweide.admin.web.components.BetaBadge
import at.theatervogelweide.admin.web.components.Bem
import at.theatervogelweide.admin.web.components.ButtonVariant
import at.theatervogelweide.admin.web.components.ReorderAnnouncement
import at.theatervogelweide.admin.web.components.ReorderController
import at.theatervogelweide.admin.web.components.ReorderHandle
import at.theatervogelweide.admin.web.components.TokenOption
import at.theatervogelweide.admin.web.components.rememberReorder
import at.theatervogelweide.admin.web.components.reorderItem
import at.theatervogelweide.domain.TheaterTime
import at.theatervogelweide.domain.TicketSchedule
import kotlinx.datetime.LocalDateTime
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.required
import org.jetbrains.compose.web.dom.CheckboxInput
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.AttrBuilderContext
import org.jetbrains.compose.web.dom.ContentBuilder
import org.jetbrains.compose.web.dom.ElementBuilder
import org.jetbrains.compose.web.dom.TagElement
import org.w3c.dom.HTMLElement
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLInputElement

private val fieldset = Bem("admin-fieldset")
private val formGrid = Bem("admin-form-grid")
private val input = Bem("admin-input")
private val alert = Bem("admin-alert")
private val dataTable = Bem("admin-data-table")
private val castEditor = Bem("admin-cast-editor")
private val reorderBlock = Bem("admin-reorder")
private val schedule = Bem("admin-schedule")
private val card = Bem("admin-card")
private val checkbox = Bem("admin-checkbox")

private fun ActEditorState.update(change: ActForm.() -> ActForm) {
    form = form.change()
}

/** The card's fields, disabled together while the server's answer is about to replace the editor. */
@Composable
private fun EditorFields(state: ActEditorState, content: @Composable () -> Unit) {
    Fieldset({
        classes(fieldset.block)
        if (state.busy) {
            attr("disabled", "")
        }
    }) { content() }
}

@Composable
internal fun GeneralSection(state: ActEditorState) {
    val form = state.form
    AdminCard(title = "Allgemein", id = SECTION_GENERAL) {
        EditorFields(state) {
            Div({ classes(formGrid.block, formGrid.modifier("title-year")) }) {
                Div({ classes(formGrid.element("item").className, formGrid.element("item").modifier("span-3")) }) {
                    AdminTextField(
                        id = "stueck-${ActDraft.TITLE}",
                        label = "Titel",
                        value = form.title,
                        onValueChange = { state.update { copy(title = it) } },
                        error = state.errorFor(ActDraft.TITLE),
                    )
                }
                AdminTextField(
                    id = "stueck-${ActDraft.YEAR}",
                    label = "Jahr",
                    value = form.yearText,
                    onValueChange = { state.update { copy(yearText = it) } },
                    error = state.errorFor(ActDraft.YEAR),
                    inputMode = "numeric",
                    autocomplete = "off",
                )
            }
            AdminMarkdownField(
                id = "stueck-${ActDraft.DESCRIPTION}",
                label = "Beschreibung",
                value = form.description,
                onValueChange = { state.update { copy(description = it) } },
                error = state.errorFor(ActDraft.DESCRIPTION),
            )
            val shortTextLength = form.shortText.trim().length
            AdminTextAreaField(
                id = "stueck-${ActDraft.SHORT_TEXT}",
                label = "Kurztext",
                value = form.shortText,
                onValueChange = { state.update { copy(shortText = it) } },
                rows = 3,
                // counts as one types; over the limit the count becomes the error, before saving
                error = if (shortTextLength > ActDraft.SHORT_TEXT_LENGTH) {
                    "Bitte höchstens ${ActDraft.SHORT_TEXT_LENGTH} Zeichen (jetzt $shortTextLength)."
                } else {
                    state.errorFor(ActDraft.SHORT_TEXT)
                },
                hint = "Für die Startseite und Link-Vorschauen. $shortTextLength von ${ActDraft.SHORT_TEXT_LENGTH} Zeichen.",
                optional = true,
                beta = true,
            )
        }
    }
}

@Composable
internal fun PerformancesSection(state: ActEditorState) {
    val form = state.form
    val premiereKey = form.premiereKey
    AdminCard(title = "Termine & Karten", id = SECTION_PERFORMANCES) {
        EditorFields(state) {
            P({ classes(card.element("hint").className) }) {
                Text("Die Seite „Kartenreservierung“ entsteht aus diesen Angaben; die früheste Vorstellung ist die Premiere.")
            }

            state.errorFor(ActDraft.PERFORMANCES)?.let {
                P({
                    classes(alert.block)
                    attr("role", "alert")
                }) { Text(it) }
            }

            if (form.performances.isNotEmpty()) {
                Div({ classes(dataTable.block) }) {
                    Table({ classes(dataTable.element("table").className) }) {
                        Thead {
                            Tr {
                                listOf("#", "Datum", "Beginn", "Ausverkauft").forEach { header ->
                                    Th({
                                        classes(dataTable.element("header-cell").className)
                                        attr("scope", "col")
                                    }) {
                                        Text(header)
                                        // only the new website marks sold-out performances (#144)
                                        if (header == "Ausverkauft") {
                                            BetaBadge()
                                        }
                                    }
                                }
                                Th({
                                    classes(dataTable.element("header-cell").className)
                                    attr("scope", "col")
                                }) { Span({ classes("admin-visually-hidden") }) { Text("Aktion") } }
                            }
                        }
                        Tbody {
                            form.performances.forEachIndexed { index, row ->
                                key(row.key) {
                                    PerformanceTableRow(state, row, number = index + 1, premiere = row.key == premiereKey)
                                }
                            }
                        }
                    }
                }
            }
            AdminButton(variant = ButtonVariant.ADD, onClick = { state.update { addPerformance() } }) { Text("+ Vorstellung") }

            Div({ classes(formGrid.block) }) {
                AdminDateField(
                    id = "stueck-${ActDraft.TICKET_SALE_START}",
                    label = "Kartenverkauf ab",
                    text = form.saleDateText,
                    onTextChange = { state.update { copy(saleDateText = it) } },
                    error = state.errorFor(ActDraft.TICKET_SALE_START),
                    optional = true,
                )
                AdminTimeField(
                    id = "stueck-$SALE_TIME",
                    label = "Uhrzeit",
                    text = form.saleTimeText,
                    onTextChange = { state.update { copy(saleTimeText = it) } },
                    error = state.errorFor(SALE_TIME),
                    optional = true,
                )
            }
            AdminTextField(
                id = "stueck-${ActDraft.RESERVATION_URL}",
                label = "Link zur Reservierung",
                value = form.reservationUrl,
                onValueChange = { state.update { copy(reservationUrl = it) } },
                type = InputType.Url,
                error = state.errorFor(ActDraft.RESERVATION_URL),
                hint = "Die Kartenseite verlinkt ihn als „Zur Kartenreservierung hier klicken“.",
                optional = true,
                placeholder = "https://…",
                inputMode = "url",
            )

            TicketSchedule(form)
        }
    }
}

@Composable
internal fun VenueSection(state: ActEditorState) {
    val form = state.form
    // the title's badge covers the venue's fields (#144)
    AdminCard(title = "Spielstätte & Eintritt", id = SECTION_VENUE, beta = true) {
        EditorFields(state) {
            AdminTextField(
                id = "stueck-${ActDraft.VENUE_NAME}",
                label = "Spielstätte",
                value = form.venueName,
                onValueChange = { state.update { copy(venueName = it) } },
                error = state.errorFor(ActDraft.VENUE_NAME),
                optional = true,
            )
            AdminTextAreaField(
                id = "stueck-${ActDraft.VENUE_ADDRESS}",
                label = "Adresse",
                value = form.venueAddress,
                onValueChange = { state.update { copy(venueAddress = it) } },
                rows = 2,
                error = state.errorFor(ActDraft.VENUE_ADDRESS),
                optional = true,
            )
            AdminMarkdownField(
                id = "stueck-${ActDraft.ADMISSION}",
                label = "Eintritt",
                value = form.admission,
                onValueChange = { state.update { copy(admission = it) } },
                error = state.errorFor(ActDraft.ADMISSION),
                rows = 4,
                optional = true,
            )
            if (state.isNew) {
                P({ classes(card.element("hint").className) }) {
                    Text("Ein neues Stück übernimmt Spielstätte und Eintritt vom letzten Stück.")
                }
            }
        }
    }
}

@Composable
private fun PerformanceTableRow(state: ActEditorState, row: PerformanceRow, number: Int, premiere: Boolean) {
    val dateField = ActForm.performanceDateField(row.key)
    val timeField = ActForm.performanceTimeField(row.key)
    Tr({ classes(dataTable.element("row").className) }) {
        Td({ classes(dataTable.element("cell").className, dataTable.element("cell").modifier("number")) }) { Text(number.toString()) }
        Td({ classes(dataTable.element("cell").className) }) {
            CellInput(
                id = "stueck-$dateField",
                label = "Datum Vorstellung $number",
                value = row.dateText,
                placeholder = "TT.MM.JJJJ",
                error = state.errorFor(dateField),
                onValueChange = { text -> state.update { updatePerformance(row.key) { copy(dateText = text) } } },
                // reads the row as it is now: the blur may come before the last input was composed
                onBlur = { state.update { updatePerformance(row.key) { copy(dateText = TheaterTime.parseDate(dateText)?.let(TheaterTime::format) ?: dateText) } } },
            )
        }
        Td({ classes(dataTable.element("cell").className) }) {
            CellInput(
                id = "stueck-$timeField",
                label = "Beginn Vorstellung $number",
                value = row.timeText,
                placeholder = "HH:MM",
                error = state.errorFor(timeField),
                onValueChange = { text -> state.update { updatePerformance(row.key) { copy(timeText = text) } } },
                onBlur = { state.update { updatePerformance(row.key) { copy(timeText = TheaterTime.parseTime(timeText)?.let(TheaterTime::format) ?: timeText) } } },
            )
            if (premiere) {
                Text(" ")
                AdminBadge("Premiere", BadgeVariant.TAG)
            }
        }
        Td({ classes(dataTable.element("cell").className) }) {
            Label(attrs = { classes(checkbox.block) }) {
                CheckboxInput(row.soldOut) {
                    classes(checkbox.element("input").className)
                    id("stueck-performance-${row.key}-soldOut")
                    onInput { event -> state.update { updatePerformance(row.key) { copy(soldOut = event.value) } } }
                }
                Span({ classes("admin-visually-hidden") }) { Text("Vorstellung $number ausverkauft") }
            }
        }
        Td({ classes(dataTable.element("cell").className) }) {
            AdminButton(
                variant = ButtonVariant.ICON,
                ariaLabel = "Vorstellung $number entfernen",
                onClick = { state.update { removePerformance(row.key) } },
            ) { Span({ attr("aria-hidden", "true") }) { Text("×") } }
        }
    }
}

/** An input inside a table cell or a cast row, labelled for screen readers only, with its error below. */
@Composable
private fun CellInput(
    id: String,
    label: String,
    value: String,
    placeholder: String,
    error: String?,
    onValueChange: (String) -> Unit,
    onBlur: () -> Unit,
) {
    Input(InputType.Text) {
        classes(input.block, input.modifier("compact"))
        id(id)
        value(value)
        attr("aria-label", label)
        attr("placeholder", placeholder)
        attr("inputmode", "numeric")
        attr("autocomplete", "off")
        onInput { onValueChange(it.value) }
        onBlur { onBlur() }
        if (error != null) {
            attr("aria-invalid", "true")
            attr("aria-describedby", "$id-message")
        }
    }
    error?.let {
        Span({
            classes(dataTable.element("cell-error").className)
            id("$id-message")
        }) { Text(it) }
    }
}

/** The milestones on the ticket page and one sentence about them, as typed so far. */
@Composable
private fun TicketSchedule(form: ActForm) {
    val ticketSchedule = form.ticketSchedule

    if (ticketSchedule != null) {
        TicketScheduleSteps(ticketSchedule, now())
    }

    AdminCallout {
        when {
            ticketSchedule != null -> {
                Text("Auf der Kartenseite: angekündigt ab ")
                Strong { Text(formatDateTime(ticketSchedule.announcedFrom)) }
                Text(", reservierbar ab ")
                Strong { Text(formatDateTime(ticketSchedule.ticketSaleStart)) }
                Text(", ausgeblendet mit Beginn der letzten Vorstellung am ")
                Strong { Text(formatDateTime(ticketSchedule.lastPerformance)) }
                Text(". Haben mehrere Stücke zugleich Kartenverkauf, zeigt sie das mit der nächsten Vorstellung.")
            }

            form.performances.isEmpty() -> Text("Ohne Vorstellungen steht das Stück nur im Archiv, nicht auf der Kartenseite.")

            else -> Text("Ohne „Kartenverkauf ab“ steht das Stück nur im Archiv, nicht auf der Kartenseite.")
        }
    }
}

@Composable
internal fun CastSection(state: ActEditorState, people: List<CastPerson>?) {
    val form = state.form
    val peopleById = (state.loaded?.castPeople.orEmpty() + people.orEmpty()).associateBy { it.id }
    val options = people?.map { TokenOption(it.id, it.name) }
    fun nameOf(key: Int) = (form.cast + form.crew).firstOrNull { it.key == key }?.name?.ifBlank { null } ?: "Rolle"
    // one list across both groups, so a role can be dragged into the other group or moved over its edge with Alt+↑/↓
    val reorder = rememberReorder(
        keys = form.castOrder,
        describe = ::nameOf,
        announce = { key ->
            val current = state.form
            val group = current.groupOf(key) ?: CastGroup.CAST
            val roles = current.rolesOf(group)
            "${nameOf(key)}: ${group.title}, Position ${roles.indexOfFirst { it.key == key } + 1} von ${roles.size}"
        },
    ) { from, to -> state.update { moveRole(from, to) } }

    AdminCard(
        title = "Besetzung",
        id = SECTION_CAST,
        header = { Span({ classes(card.element("hint").className) }) { Text("Zeilen ziehen oder mit Alt und ↑/↓ verschieben") } },
    ) {
        EditorFields(state) {
            state.errorFor(ActDraft.CAST)?.let {
                P({
                    classes(alert.block)
                    attr("role", "alert")
                }) { Text(it) }
            }
            if (form.cast.isEmpty() && form.crew.isEmpty()) {
                P({ classes(card.element("hint").className) }) {
                    Text("Noch keine Besetzung. Die Website zeigt dann, dass sie noch bekanntgegeben wird.")
                }
            }
            Div({ classes(castEditor.block) }) {
                CastGroup.entries.forEach { group ->
                    CastGroupSection(state, group, peopleById, options, reorder)
                }
                ReorderAnnouncement(reorder)
            }
            if (form.crew.none { it.name.trim() == DIRECTOR }) {
                P({ classes(card.element("hint").className) }) {
                    Text("Die Website nennt die Regie, sobald es unter „Hinter der Bühne“ eine Rolle „$DIRECTOR“ gibt.")
                }
            }
        }
    }
}

/** The role the public site names as the director, in the crew. */
private const val DIRECTOR = "Regie"

@Composable
private fun CastGroupSection(
    state: ActEditorState,
    group: CastGroup,
    peopleById: Map<Long, CastPerson>,
    options: List<TokenOption<Long>>?,
    reorder: ReorderController<Int>,
) {
    val roles = state.form.rolesOf(group)
    val titleId = "stueck-${group.name.lowercase()}-titel"
    Section({
        classes(castEditor.element("group").className)
        attr("aria-labelledby", titleId)
    }) {
        H3({
            classes(castEditor.element("group-title").className)
            id(titleId)
        }) {
            Text(group.title)
            // only the new website lists the crew apart (#144)
            if (group == CastGroup.CREW) {
                BetaBadge()
            }
        }
        if (roles.isEmpty()) {
            // a drop target too, so a role can be dragged into an empty group
            P({
                classes(card.element("hint").className)
                reorderItem(reorder, ActForm.GROUP_BOUNDARY)
            }) { Text("Noch keine Rollen.") }
        } else {
            Ul({ classes(castEditor.element("list").className, reorderBlock.block) }) {
                roles.forEachIndexed { index, row ->
                    key(row.key) {
                        val field = when (group) {
                            CastGroup.CAST -> ActDraft.castField(index)
                            CastGroup.CREW -> ActDraft.crewField(index)
                        }
                        RoleRow(state, row, group, field, peopleById, options, reorder)
                    }
                }
            }
        }
        Div({ classes(castEditor.element("actions").className) }) {
            AdminButton(variant = ButtonVariant.ADD, onClick = {
                state.update { addRole(group) }
                state.focusCastRow = state.form.rolesOf(group).last().key
            }) { Text("+ Rolle") }
        }
    }
}

@Composable
private fun RoleRow(
    state: ActEditorState,
    row: CastRow,
    group: CastGroup,
    errorField: String,
    peopleById: Map<Long, CastPerson>,
    options: List<TokenOption<Long>>?,
    reorder: ReorderController<Int>,
) {
    val error = state.errorFor(errorField)
    val name = row.name.ifBlank { "neue Rolle" }
    val inputId = "stueck-rolle-${row.key}"

    Li({
        classes(castEditor.element("row").className)
        reorderItem(reorder, row.key)
    }) {
        ReorderHandle(reorder, row.key, "Rolle „$name“ verschieben")
        Input(InputType.Text) {
            classes(input.block, input.modifier("compact"), castEditor.element("role").className)
            id(inputId)
            value(row.name)
            attr("aria-label", "Rolle")
            attr(
                "placeholder",
                when (group) {
                    CastGroup.CAST -> "Rolle, z. B. Walther"
                    CastGroup.CREW -> "Funktion, z. B. Regie"
                },
            )
            required()
            onInput { event -> state.update { updateCastRow(row.key) { copy(name = event.value) } } }
            if (error != null) {
                attr("aria-invalid", "true")
                attr("aria-describedby", "$inputId-message")
            }
            ref { element: HTMLInputElement ->
                if (state.focusCastRow == row.key) {
                    state.focusCastRow = null
                    element.focus()
                }
                onDispose { }
            }
        }
        AdminTokenInput(
            id = "stueck-personen-${row.key}",
            label = "Personen für „$name“",
            selected = row.personIds.map { id -> TokenOption(id, peopleById[id]?.name ?: "Person $id") },
            options = options,
            onAdd = { id -> state.update { updateCastRow(row.key) { copy(personIds = personIds + id) } } },
            onRemove = { id -> state.update { updateCastRow(row.key) { copy(personIds = personIds - id) } } },
            placeholder = if (row.personIds.isEmpty()) "Person suchen … (sonst „nicht besetzt“)" else "Person suchen …",
            noMatches = "Keine Person gefunden.",
        )
        AdminButton(
            variant = ButtonVariant.ICON,
            ariaLabel = "Rolle „$name“ entfernen",
            onClick = { state.update { removeCastRow(row.key) } },
        ) { Span({ attr("aria-hidden", "true") }) { Text("×") } }
        error?.let {
            Span({
                classes(castEditor.element("row-error").className)
                id("$inputId-message")
            }) { Text(it) }
        }
    }
}

@Composable
internal fun ImagesSection(state: ActEditorState, api: AdminApi) {
    AdminCard(title = "Bilder", id = SECTION_IMAGES) {
        EditorFields(state) {
            AdminSingleImageField(
                id = "stueck-${ActDraft.MAIN_IMAGE}",
                label = "Titelbild",
                images = state.mainImage,
                imageUrl = { api.imageUrl(ImageCategory.ACTS, it) },
                hint = "Steht oben auf der Seite des Stücks im Archiv.",
                error = state.errorFor(ActDraft.MAIN_IMAGE),
                disabled = state.busy,
            )
            AdminImageUploader(
                id = "stueck-${ActDraft.IMAGES}",
                label = "Weitere Bilder",
                images = state.images,
                imageUrl = { api.imageUrl(ImageCategory.ACTS, it) },
                firstIsMain = false,
                disabled = state.busy,
                error = state.errorFor(ActDraft.IMAGES),
            )
            AdminCallout { Text("Die Website zeigt derzeit nur das Titelbild; die weiteren Bilder sind hier aufbewahrt.") }
        }
    }
}

/** `<strong>`, which Compose HTML has no function for; the design system marks key values with it. */
@Composable
internal fun Strong(attrs: AttrBuilderContext<HTMLElement>? = null, content: ContentBuilder<HTMLElement>? = null) {
    TagElement(elementBuilder = strong, applyAttrs = attrs, content = content)
}

private val strong = ElementBuilder.createBuilder<HTMLElement>("strong")

/** The four milestones of [ticketSchedule] on the ticket page, those before [now] marked as done. */
@Composable
fun TicketScheduleSteps(ticketSchedule: TicketSchedule, now: LocalDateTime) {
    val steps = listOf(
        "Angekündigt ab" to ticketSchedule.announcedFrom,
        "Kartenverkauf ab" to ticketSchedule.ticketSaleStart,
        "Premiere" to ticketSchedule.premiere,
        "Letzte Vorstellung" to ticketSchedule.lastPerformance,
    )
    Ol({
        classes(schedule.block)
        attr("aria-label", "Zeitplan der Kartenseite")
    }) {
        steps.forEachIndexed { index, (label, at) ->
            val done = at <= now
            Li({
                classes(schedule.element("step").className)
                if (done) {
                    attr("data-state", "done")
                }
            }) {
                Span({ classes(schedule.element("label").className) }) { Text(label) }
                Strong({ classes(schedule.element("date").className) }) { Text(formatDateTime(at)) }
                Span({ classes(schedule.element("state").className) }) {
                    Text(if (index == steps.lastIndex && !done) "danach automatisch ausgeblendet" else relativeState(at, now))
                }
            }
        }
    }
}
